import * as XLSX from "xlsx";
import { saveFileWithPicker } from "../../utils/fileManagerFunctions";
import { generateFilename } from "./download-utils/generateFilenameToExport";

// 🆕 Dropdown options extracted from uploaded DAST report
const DAST_DROPDOWN_OPTIONS = {
  "Vulnerability Status": ["TP", "FP"], // True Positive, False Positive

  Severity: ["Critical", "High", "Medium", "Low"],

  "Attack Vector (AV)": [
    "Network (N)",
    "Adjacent (A)",
    "Local (L)",
    "Physical (P)",
  ],

  "Attack Complexity (AC)": ["Low (L)", "High (H)"],

  "Privileges Required (PR)": ["None (N)", "Low (L)", "High (H)"],

  "User Interaction (UI)": ["None (N)", "Required (R)"],

  "Scope (S)": ["Unchanged (U)", "Changed (C)"],

  "Confidentiality Impact (C)": ["None (N)", "Low (L)", "High (H)"],

  "Integrity Impact (I)": ["None (N)", "Low (L)", "High (H)"],

  "Availability Impact (A)": ["None (N)", "Low (L)", "High (H)"],

  "Exploit Code Maturity (E)": [
    "Not Defined (X)",
    "Unproven (U)",
    "Proof-of-Concept (P)",
    "Functional (F)",
    "High (H)",
  ],

  "Remediation Level (RL)": [
    "Not Defined (X)",
    "Official Fix (O)",
    "Temporary Fix (T)",
    "Workaround (W)",
    "Unavailable (U)",
  ],

  "Report Confidence (RC)": [
    "Not Defined (X)",
    "Unknown (U)",
    "Reasonable (R)",
    "Confirmed (C)",
  ],

  // Environmental metrics (Modified versions)
  "Modified Attack Vector (MAV)": [
    "Not Defined (X)",
    "Network (N)",
    "Adjacent (A)",
    "Local (L)",
    "Physical (P)",
  ],

  "Modified Attack Complexity (MAC)": [
    "Not Defined (X)",
    "Low (L)",
    "High (H)",
  ],

  "Modified Privileges Required (MPR)": [
    "Not Defined (X)",
    "None (N)",
    "Low (L)",
    "High (H)",
  ],

  "Modified User Interaction (MUI)": [
    "Not Defined (X)",
    "None (N)",
    "Required (R)",
  ],

  "Modified Scope (MS)": ["Not Defined (X)", "Unchanged (U)", "Changed (C)"],

  "Modified Confidentiality (MC)": [
    "Not Defined (X)",
    "None (N)",
    "Low (L)",
    "High (H)",
  ],

  "Modified Integrity (MI)": [
    "Not Defined (X)",
    "None (N)",
    "Low (L)",
    "High (H)",
  ],

  "Modified Availability (MA)": [
    "Not Defined (X)",
    "None (N)",
    "Low (L)",
    "High (H)",
  ],

  "Confidentiality Requirement (CR)": [
    "Not Defined (X)",
    "Low (L)",
    "Medium (M)",
    "High (H)",
  ],

  "Integrity Requirement (IR)": [
    "Not Defined (X)",
    "Low (L)",
    "Medium (M)",
    "High (H)",
  ],

  "Availability Requirement (AR)": [
    "Not Defined (X)",
    "Low (L)",
    "Medium (M)",
    "High (H)",
  ],
};

// 🆕 Create DAST Excel format matching uploaded file structure
function createDastExcelFormat(scanData) {
  // Header structure matching uploaded file
  const mainHeaders = [
    "Vulnerabilities Data",
    "",
    "",
    "",
    "",
    "",
    "",
    "",
    "CVSS Vectors",
    "",
    "",
    "",
    "",
    "",
    "",
    "",
    "Temporal",
    "",
    "",
    "Environmental",
    "",
    "",
    "",
    "",
    "",
    "",
    "",
    "",
    "",
  ];

  const subHeaders = [
    "",
    "",
    "",
    "",
    "",
    "",
    "",
    "",
    "Base Metrics",
    "",
    "",
    "",
    "",
    "",
    "",
    "",
    "Temporal",
    "",
    "",
    "Environmental",
    "",
    "",
    "",
    "",
    "",
    "",
    "",
    "",
    "",
  ];

  const columnHeaders = [
    "Vulnerability Name",
    "Description",
    "Instances",
    "Evidence",
    "Vulnerability Status",
    "Severity",
    "Remediation",
    "Vulnerability Identified Date",
    "Attack Vector (AV)",
    "Attack Complexity (AC)",
    "Privileges Required (PR)",
    "User Interaction (UI)",
    "Scope (S)",
    "Confidentiality Impact (C)",
    "Integrity Impact (I)",
    "Availability Impact (A)",
    "Exploit Code Maturity (E)",
    "Remediation Level (RL)",
    "Report Confidence (RC)",
    "Modified Attack Vector (MAV)",
    "Modified Attack Complexity (MAC)",
    "Modified Privileges Required (MPR)",
    "Modified User Interaction (MUI)",
    "Modified Scope (MS)",
    "Modified Confidentiality (MC)",
    "Modified Integrity (MI)",
    "Modified Availability (MA)",
    "Confidentiality Requirement (CR)",
    "Integrity Requirement (IR)",
    "Availability Requirement (AR)",
  ];

  const excelData = [mainHeaders, subHeaders, columnHeaders];

  // Process each vulnerability
  scanData.forEach((vulnerability, vulnIndex) => {
    const instances = vulnerability.instances || [{ url: "", evidence: "" }];

    instances.forEach((instance, instIndex) => {
      const row = new Array(30).fill("");

      if (instIndex === 0) {
        // First instance row contains all vulnerability details
        row[0] = vulnerability["Vulnerability Name"] || "";
        row[1] = vulnerability.Description || "";
        row[2] = formatInstanceUrl(instance, instIndex + 1);
        row[3] = formatEvidence(instance, instIndex + 1);
        row[4] = getVulnerabilityStatus(vulnerability.Severity);
        row[5] = vulnerability.Severity || "";
        row[6] =
          vulnerability.Remediation ||
          vulnerability["Recommended Solutions"] ||
          "";
        row[7] = getCurrentDate();

        // CVSS Base Metrics
        const cvssMetrics = getCvssMetrics(vulnerability.Severity);
        row[8] = cvssMetrics.attackVector;
        row[9] = cvssMetrics.attackComplexity;
        row[10] = cvssMetrics.privilegesRequired;
        row[11] = cvssMetrics.userInteraction;
        row[12] = cvssMetrics.scope;
        row[13] = cvssMetrics.confidentialityImpact;
        row[14] = cvssMetrics.integrityImpact;
        row[15] = cvssMetrics.availabilityImpact;

        // Temporal Metrics
        row[16] = cvssMetrics.exploitCodeMaturity;
        row[17] = cvssMetrics.remediationLevel;
        row[18] = cvssMetrics.reportConfidence;

        // Environmental Metrics
        row[19] = cvssMetrics.modifiedAttackVector;
        row[20] = cvssMetrics.modifiedAttackComplexity;
        row[21] = cvssMetrics.modifiedPrivilegesRequired;
        row[22] = cvssMetrics.modifiedUserInteraction;
        row[23] = cvssMetrics.modifiedScope;
        row[24] = cvssMetrics.modifiedConfidentiality;
        row[25] = cvssMetrics.modifiedIntegrity;
        row[26] = cvssMetrics.modifiedAvailability;
        row[27] = cvssMetrics.confidentialityRequirement;
        row[28] = cvssMetrics.integrityRequirement;
        row[29] = cvssMetrics.availabilityRequirement;
      } else {
        // Additional instance rows only contain instance-specific data
        row[0] = "";
        row[1] = "";
        row[2] = formatInstanceUrl(instance, instIndex + 1);
        row[3] = formatEvidence(instance, instIndex + 1);
        // All other fields remain empty for additional instances
      }

      excelData.push(row);
    });
  });

  return excelData;
}

// 🆕 Format instance URL with numbering
function formatInstanceUrl(instance, index) {
  if (!instance || !instance.url) return "";
  return `${index}. ${instance.url}`;
}

// 🆕 Format evidence with numbering
function formatEvidence(instance, index) {
  if (!instance || !instance.evidence) return "";
  return `${index}. ${instance.evidence}`;
}

// 🆕 Get vulnerability status based on severity
function getVulnerabilityStatus(severity) {
  const severityLower = (severity || "").toLowerCase();
  if (["critical", "high", "medium"].includes(severityLower)) {
    return "TP"; // True Positive
  }
  return "FP"; // False Positive for Low severity
}

// 🆕 Get current date in MM/DD/YYYY format
function getCurrentDate() {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  const year = now.getFullYear();
  return `${month}/${day}/${year} 00:00:00`;
}

// 🆕 Generate CVSS metrics based on severity
function getCvssMetrics(severity) {
  const severityLower = (severity || "").toLowerCase();

  const metrics = {
    critical: {
      attackVector: "Network (N)",
      attackComplexity: "Low (L)",
      privilegesRequired: "Low (L)",
      userInteraction: "None (N)",
      scope: "Changed (C)",
      confidentialityImpact: "Low (L)",
      integrityImpact: "High (H)",
      availabilityImpact: "High (H)",
      exploitCodeMaturity: "Proof-of-Concept (P)",
      remediationLevel: "Official Fix (O)",
      reportConfidence: "Reasonable (R)",
      modifiedAttackVector: "Network (N)",
      modifiedAttackComplexity: "Low (L)",
      modifiedPrivilegesRequired: "Low (L)",
      modifiedUserInteraction: "None (N)",
      modifiedScope: "Changed (C)",
      modifiedConfidentiality: "High (H)",
      modifiedIntegrity: "High (H)",
      modifiedAvailability: "Low (L)",
      confidentialityRequirement: "High (H)",
      integrityRequirement: "High (H)",
      availabilityRequirement: "High (H)",
    },
    high: {
      attackVector: "Network (N)",
      attackComplexity: "Low (L)",
      privilegesRequired: "Low (L)",
      userInteraction: "None (N)",
      scope: "Unchanged (U)",
      confidentialityImpact: "High (H)",
      integrityImpact: "Low (L)",
      availabilityImpact: "High (H)",
      exploitCodeMaturity: "Proof-of-Concept (P)",
      remediationLevel: "Official Fix (O)",
      reportConfidence: "Reasonable (R)",
      modifiedAttackVector: "Network (N)",
      modifiedAttackComplexity: "Low (L)",
      modifiedPrivilegesRequired: "Low (L)",
      modifiedUserInteraction: "None (N)",
      modifiedScope: "Unchanged (U)",
      modifiedConfidentiality: "High (H)",
      modifiedIntegrity: "High (H)",
      modifiedAvailability: "Low (L)",
      confidentialityRequirement: "High (H)",
      integrityRequirement: "High (H)",
      availabilityRequirement: "Medium (M)",
    },
    medium: {
      attackVector: "Network (N)",
      attackComplexity: "Low (L)",
      privilegesRequired: "Low (L)",
      userInteraction: "Required (R)",
      scope: "Unchanged (U)",
      confidentialityImpact: "Low (L)",
      integrityImpact: "Low (L)",
      availabilityImpact: "High (H)",
      exploitCodeMaturity: "Proof-of-Concept (P)",
      remediationLevel: "Official Fix (O)",
      reportConfidence: "Reasonable (R)",
      modifiedAttackVector: "Network (N)",
      modifiedAttackComplexity: "Low (L)",
      modifiedPrivilegesRequired: "Low (L)",
      modifiedUserInteraction: "Required (R)",
      modifiedScope: "Unchanged (U)",
      modifiedConfidentiality: "Low (L)",
      modifiedIntegrity: "Low (L)",
      modifiedAvailability: "High (H)",
      confidentialityRequirement: "Medium (M)",
      integrityRequirement: "Medium (M)",
      availabilityRequirement: "Medium (M)",
    },
    low: {
      attackVector: "Network (N)",
      attackComplexity: "High (H)",
      privilegesRequired: "High (H)",
      userInteraction: "None (N)",
      scope: "Unchanged (U)",
      confidentialityImpact: "Low (L)",
      integrityImpact: "None (N)",
      availabilityImpact: "Low (L)",
      exploitCodeMaturity: "Proof-of-Concept (P)",
      remediationLevel: "Official Fix (O)",
      reportConfidence: "Confirmed (C)",
      modifiedAttackVector: "Network (N)",
      modifiedAttackComplexity: "High (H)",
      modifiedPrivilegesRequired: "None (N)",
      modifiedUserInteraction: "None (N)",
      modifiedScope: "Unchanged (U)",
      modifiedConfidentiality: "Low (L)",
      modifiedIntegrity: "None (N)",
      modifiedAvailability: "Low (L)",
      confidentialityRequirement: "Medium (M)",
      integrityRequirement: "Medium (M)",
      availabilityRequirement: "Low (L)",
    },
  };

  return metrics[severityLower] || metrics.low;
}

// 🆕 Apply DAST-specific formatting
function applyDastFormatting(worksheet, data) {
  const range = XLSX.utils.decode_range(worksheet["!ref"]);

  // Set column widths
  const columnWidths = [
    { wch: 25 }, // Vulnerability Name
    { wch: 50 }, // Description
    { wch: 40 }, // Instances
    { wch: 30 }, // Evidence
    { wch: 15 }, // Status
    { wch: 12 }, // Severity
    { wch: 40 }, // Remediation
    { wch: 20 }, // Date
    ...Array(22).fill({ wch: 15 }), // CVSS columns
  ];
  worksheet["!cols"] = columnWidths;

  // Apply cell formatting
  for (let row = range.s.r; row <= range.e.r; row++) {
    for (let col = range.s.c; col <= range.e.c; col++) {
      const cellAddress = XLSX.utils.encode_cell({ r: row, c: col });
      if (worksheet[cellAddress]) {
        worksheet[cellAddress].s = {
          alignment: {
            wrapText: true,
            vertical: "top",
            horizontal: "left",
          },
          font: {
            name: "Calibri",
            sz: 11,
            bold: row < 3, // Make headers bold
          },
          border: {
            top: { style: "thin" },
            bottom: { style: "thin" },
            left: { style: "thin" },
            right: { style: "thin" },
          },
        };

        // Header row styling
        if (row < 3) {
          worksheet[cellAddress].s.fill = {
            fgColor: { rgb: "E6E6FA" },
          };
        }
      }
    }
  }

  // Set row heights
  worksheet["!rows"] = data.map((_, index) => ({
    hpt: index < 3 ? 25 : 40, // Headers shorter, data rows taller
  }));
}

// 🆕 Add dropdown validations to specific columns
function addDropdownValidations(worksheet, totalRows) {
  const dropdownColumns = {
    4: "Vulnerability Status", // Column E (0-indexed)
    5: "Severity", // Column F
    8: "Attack Vector (AV)", // Column I
    9: "Attack Complexity (AC)", // Column J
    10: "Privileges Required (PR)", // Column K
    11: "User Interaction (UI)", // Column L
    12: "Scope (S)", // Column M
    13: "Confidentiality Impact (C)", // Column N
    14: "Integrity Impact (I)", // Column O
    15: "Availability Impact (A)", // Column P
    16: "Exploit Code Maturity (E)", // Column Q
    17: "Remediation Level (RL)", // Column R
    18: "Report Confidence (RC)", // Column S
    19: "Modified Attack Vector (MAV)", // Column T
    20: "Modified Attack Complexity (MAC)", // Column U
    21: "Modified Privileges Required (MPR)", // Column V
    22: "Modified User Interaction (MUI)", // Column W
    23: "Modified Scope (MS)", // Column X
    24: "Modified Confidentiality (MC)", // Column Y
    25: "Modified Integrity (MI)", // Column Z
    26: "Modified Availability (MA)", // Column AA
    27: "Confidentiality Requirement (CR)", // Column AB
    28: "Integrity Requirement (IR)", // Column AC
    29: "Availability Requirement (AR)", // Column AD
  };

  // Create validation ranges for each dropdown column
  Object.entries(dropdownColumns).forEach(([colIndex, fieldName]) => {
    const columnLetter = XLSX.utils.encode_col(parseInt(colIndex));
    const range = `${columnLetter}4:${columnLetter}${totalRows}`; // Start from row 4 (after headers)

    const options = DAST_DROPDOWN_OPTIONS[fieldName];
    if (options && options.length > 0) {
      // Create data validation for the range
      if (!worksheet["!dataValidation"]) {
        worksheet["!dataValidation"] = [];
      }

      worksheet["!dataValidation"].push({
        type: "list",
        allowBlank: true,
        showInputMessage: true,
        showErrorMessage: true,
        errorTitle: "Invalid Selection",
        error: `Please select a value from the dropdown list for ${fieldName}`,
        promptTitle: `Select ${fieldName}`,
        prompt: `Choose from available options for ${fieldName}`,
        formula1: `"${options.join(",")}"`,
        sqref: range,
      });
    }
  });
}

// 🆕 Create a separate sheet with dropdown reference data
function createDropdownReferenceSheet(workbook) {
  const referenceData = [];

  // Create header row
  const headers = Object.keys(DAST_DROPDOWN_OPTIONS);
  const maxRows = Math.max(
    ...Object.values(DAST_DROPDOWN_OPTIONS).map((arr) => arr.length)
  );

  // Add headers
  referenceData.push(headers);

  // Add data rows
  for (let i = 0; i < maxRows; i++) {
    const row = headers.map((header) => {
      const options = DAST_DROPDOWN_OPTIONS[header];
      return i < options.length ? options[i] : "";
    });
    referenceData.push(row);
  }

  const refWorksheet = XLSX.utils.aoa_to_sheet(referenceData);

  // Format reference sheet
  const range = XLSX.utils.decode_range(refWorksheet["!ref"]);
  for (let row = range.s.r; row <= range.e.r; row++) {
    for (let col = range.s.c; col <= range.e.c; col++) {
      const cellAddress = XLSX.utils.encode_cell({ r: row, c: col });
      if (refWorksheet[cellAddress]) {
        refWorksheet[cellAddress].s = {
          alignment: { wrapText: true, vertical: "top", horizontal: "left" },
          font: { name: "Calibri", sz: 10, bold: row === 0 },
          fill: row === 0 ? { fgColor: { rgb: "D3D3D3" } } : undefined,
        };
      }
    }
  }

  // Set column widths
  refWorksheet["!cols"] = headers.map(() => ({ wch: 25 }));

  XLSX.utils.book_append_sheet(workbook, refWorksheet, "Dropdown_Options");
}

// 🆕 Enhanced Excel export with dropdown validation
export const exportToDastXLSWithDropdowns = (
  serviceResponse,
  filename,
  runType = "Run-1"
) => {
  // 🔥 FIX: Ensure we're working with flat array
  let flatServiceResponse;

  if (Array.isArray(serviceResponse)) {
    // If it's an array of arrays, flatten it
    if (Array.isArray(serviceResponse[0])) {
      flatServiceResponse = serviceResponse.flat();
    } else {
      flatServiceResponse = serviceResponse;
    }
  } else {
    flatServiceResponse = [serviceResponse];
  }

  // console.log("Processing DAST data:", flatServiceResponse); // Debug log

  const workbook = XLSX.utils.book_new();

  // Create DAST-formatted data
  const dastFormattedData = createDastExcelFormat(flatServiceResponse);

  // 🎯 UPDATED: Clean sheet name
  const sheetTitle = `DAST Run1`;
  const worksheet = XLSX.utils.aoa_to_sheet(dastFormattedData);

  // Apply dropdown validations
  addDropdownValidations(worksheet, dastFormattedData.length);

  // Apply DAST-specific formatting
  applyDastFormatting(worksheet, dastFormattedData);

  XLSX.utils.book_append_sheet(workbook, worksheet, sheetTitle);

  // Add dropdown reference sheet
  createDropdownReferenceSheet(workbook);

  const wbout = XLSX.write(workbook, {
    bookType: "xlsx",
    type: "array",
    cellStyles: true,
  });

  const blob = new Blob([wbout], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });

  // 🎯 UPDATED: Include runType in filename
  const dateStr = new Date().toISOString().split("T")[0]; // YYYY-MM-DD format
  const runTypeClean = runType.replace("-", ""); // Convert "Run-1" to "Run1"
  const extendedFileName = `DAST_Report_${runTypeClean}_${dateStr}.xlsx`;

  saveFileWithPicker(blob, extendedFileName);
};

// Main export function that determines which format to use
export const exportToXLS = (serviceResponses, filename) => {
  // Check if this is DAST data and should include dropdowns
  if (filename && filename.toLowerCase().includes("dast")) {
    return exportToDastXLSWithDropdowns(serviceResponses, filename);
  }

  // Original implementation for other formats would go here
  // For now, we'll use the DAST format as default
  return exportToDastXLSWithDropdowns(serviceResponses, filename);
};
